import Cookies from 'js-cookie'
import { v4 as uuid } from 'uuid'

import { isCookieConsent } from 'shared/utils/cookieConsent'
import { createTenantPath } from 'shared/utils/routing'
import { mapGkAirAreaToCrossSellResponse } from 'views/enhancers/withCrossSell/gkAirMapper'
import buildClientAPI from 'views/providers/clientAPI'

const GK_AIR_SID_COOKIE = 'gkasid'
const GK_AIR_CACHE_TTL_MS = 5000

const gkAirRequestCache = new Map()

const getFirstValue = value => {
  if (Array.isArray(value)) {
    return getFirstValue(value[0])
  }

  if (typeof value !== 'string') {
    return null
  }

  return (
    value
      .split(',')
      .map(item => item.trim())
      .find(Boolean) || ''
  )
}

const normalizeCommaSeparatedValue = value => {
  if (Array.isArray(value)) {
    return value
      .map(item => normalizeCommaSeparatedValue(item))
      .filter(Boolean)
      .join(',')
  }

  if (typeof value !== 'string') {
    return ''
  }

  return value
    .split(',')
    .map(item => item.trim())
    .filter(Boolean)
    .join(',')
}

const getGkAirSid = () => {
  const cookieSid = Cookies.get(GK_AIR_SID_COOKIE)

  if (cookieSid) {
    return cookieSid
  }

  const sid = uuid()

  Cookies.set(GK_AIR_SID_COOKIE, sid, { path: '/' })

  return sid
}

const getCachedGkAirRecommendations = ({ cacheKey, fetchRecommendations }) => {
  const now = Date.now()
  const cachedEntry = gkAirRequestCache.get(cacheKey)

  if (cachedEntry && cachedEntry.expiresAt > now) {
    return cachedEntry.promise
  }

  gkAirRequestCache.forEach((entry, key) => {
    if (entry.expiresAt <= now) {
      gkAirRequestCache.delete(key)
    }
  })

  const requestPromise = fetchRecommendations().catch(error => {
    if (gkAirRequestCache.get(cacheKey)?.promise === requestPromise) {
      gkAirRequestCache.delete(cacheKey)
    }

    throw error
  })

  gkAirRequestCache.set(cacheKey, {
    expiresAt: now + GK_AIR_CACHE_TTL_MS,
    promise: requestPromise,
  })

  return requestPromise
}

export const clearGkAirRequestCache = () => {
  gkAirRequestCache.clear()
}

export const fetchGkAirRecommendations = async ({
  adServerData,
  cid,
  coreShopEnvironment,
  expa,
  gkAirArea,
  language,
  locale,
  pid,
  serviceId,
  tenant,
  userId,
}) => {
  const normalizedCid = typeof cid === 'string' ? cid.trim() : ''
  const firstPid = getFirstValue(pid)
  const plist = normalizeCommaSeparatedValue(pid)
  const requestPid = firstPid === null ? undefined : firstPid
  const sid = getGkAirSid()
  const isTrackingAllowed = isCookieConsent(Cookies)
  const tenantPath = createTenantPath(tenant, coreShopEnvironment)
  const cacheKey = [
    tenantPath,
    language,
    normalizedCid,
    firstPid || '',
    plist,
    expa || '',
    sid,
    serviceId,
    isTrackingAllowed && userId ? userId : '',
  ].join('|')

  const params = {
    expa,
    sid,
    tracking: true,
    titlekey: language,
    ...(normalizedCid ? { cid: normalizedCid } : {}),
    ...(requestPid !== undefined ? { pid: requestPid } : {}),
    ...(pid !== undefined && pid !== null ? { plist } : {}),
    ...(isTrackingAllowed && userId ? { userid: userId } : {}),
  }

  const { gkAirService } = buildClientAPI()

  const gkAirResponse = await getCachedGkAirRecommendations({
    cacheKey,
    fetchRecommendations: () =>
      gkAirService.fetchRecommendations({
        serviceId,
        params,
        adServerData,
      }),
  })

  const areaData = gkAirResponse?.recommendations?.[gkAirArea]

  if (!areaData) {
    throw new Error(`Area "${gkAirArea}" is missing in GK Air response`)
  }

  return mapGkAirAreaToCrossSellResponse({
    areaData,
    areaId: gkAirArea,
    contextPid: normalizedCid ? null : firstPid || null,
    locale: locale || language,
  })
}
